/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the depot management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid, MenuItem, FormControlLabel, makeStyles, Theme } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import * as Yup from 'yup';

import { Field, FormRenderProps, useForm } from 'react-final-form';

import { DateTime } from 'luxon';

import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import { DrugDispensationActions } from '../../../store/modules/drugmanagement/drugdispensation';
import { SelectWrapper } from '../../../components/FinalForm/FinalFormControls/SelectWrapper';
import DatePickerWrapper from '../../../components/FinalForm/FinalFormControls/DatePickerWrapper';
import NumberTextField from '../../../components/FinalForm/FinalFormControls/NumberTextField';
import { selectDistinctObjects } from '../../../utilities/selectDistinctObjects';
import { getDrugUnitName } from '../../../utilities/getDrugUnitName';
import useAsyncFunction from '../../../hooks/useAsyncFunction';
import { usePatientDrugs } from '../../../store/modules/drugmanagement/patientdrugs';
import { onlinePatientManagement, PatientContext } from '@ngt/online-patient-management-ui';
import { Timepoint } from '../../../api/Neon.Dtos';
import { useDrugDispensations } from '../../../store/modules/drugmanagement/drugdispensations';
import { CheckboxWrapper } from '../../../components/FinalForm/FinalFormControls/CheckboxWrapper';
import TextFieldWrapper from '../../../components/FinalForm/FinalFormControls/TextFieldWrapper';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDrugDispensationModalProps {
    drugDispensation: Dtos.DrugDispensation | null;
    timepoints: Dtos.Timepoint[] | null;
    timepointsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugShipmentDepotBatches: Dtos.DrugShipmentDepotBatch[] | null;
    drugShipmentDepotBatchesLoadState: IRequestState<Dtos.ResponseStatus>;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IDrugDispensationFormProps extends FormRenderProps<Dtos.DrugDispensation> {
    drugDispensation?: Dtos.DrugDispensation;
    timepoints: Dtos.Timepoint[] | null;
    timepointsLoadState: IRequestState<Dtos.ResponseStatus>;
    drugShipmentDepotBatches: Dtos.DrugShipmentDepotBatch[] | null;
    drugShipmentDepotBatchesLoadState: IRequestState<Dtos.ResponseStatus>;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

interface IDrugDispensationForm extends Partial<Dtos.DrugDispensation> {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => {
    return ({
        textarea: {
            color: 'inherit',
            textDecoration: 'none',
            display: 'flex',

            '& .MuiFormHelperText-contained': {
                margin: '3px 0px 0px 0px !important',
                transitionProperty: 'width !important'
            }
        },
    })
});

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugUnitName = getDrugUnitName(true);

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const dateDispensationValidator = function (this: Yup.TestContext,
    value: string) {
    
    return value ? DateTime.fromISO(value) <= DateTime.now() : true;
}

const validation = Yup.object().shape<IDrugDispensationForm>({
    patientId: Yup.number().required(),
    pharmacyId: Yup.number().required(),
    timepointId: Yup.number().required('This field is required'),
    depotBatchId: Yup.number().required('This field is required'),
    dateDispensation: Yup.string().required('This field is required')
        .test('no-future-date',
            'This field must not be a future date',
            dateDispensationValidator),
    drugUnits: Yup.number().required('This field is required').min(0)
        .when(['timepointId'], (timepointId: number, schema: Yup.NumberSchema) => timepointId < 6 ? schema.min(1, 'Fixed Dose Administration. Only 1 vials is to be used for this timepoint.') : schema)
        .when(['timepointId'], (timepointId: number, schema: Yup.NumberSchema) => timepointId < 6 ? schema.max(1, 'Fixed Dose Administration. Only 1 vials is to be used for this timepoint.') : schema)
        .when(['timepointId'], (timepointId: number, schema: Yup.NumberSchema) => timepointId > 6 ? schema.max(5, 'Fixed Dose Administration. Only 5 vials is to be used for this timepoint.') : schema),
    notes: Yup.string().when(['voided'], (voided: boolean, schema: Yup.StringSchema) => voided === true ? schema.required('This field is required') : schema)
});

const DrugDispensationForm: React.FunctionComponent<IDrugDispensationFormProps> = ({
    drugDispensation,
    timepoints,
    timepointsLoadState,
    drugShipmentDepotBatches,
    drugShipmentDepotBatchesLoadState,
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const classes = useStyles();

    const [drugDispensations, , ] = useDrugDispensations();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {

        // do some validation for Adj. Cycles

        var formDrugUnits = formRenderProps.form.getFieldState('drugUnits')?.value ?? 0;
        var formTimepointId = formRenderProps.form.getFieldState('timepointId')?.value ?? 0; 
        var formDateDispensation = formRenderProps.form.getFieldState('dateDispensation')?.value; 

        if (formTimepointId > 0 && !!formDateDispensation) {
            var priorDispensations = drugDispensations?.filter(x => x.timepointId!! < formTimepointId);

            if (priorDispensations?.find(x => DateTime.fromISO(x.dateDispensation!!) >= DateTime.fromISO(formDateDispensation!!)) != null) {
                enqueueSnackbar(`The dispensation date must be after the previous timepoints' dispensation dates.`, { variant: 'error' });
                return;
            }
        }

        if (formTimepointId > 6)
        {
            var otherDispensations = drugDispensations?.filter(x => x.timepointId === formTimepointId && x.id !== drugDispensation?.id).map(x => x.drugUnits).reduce((accumulator, value) => accumulator! + value!, 0) ?? 0;

            if ((otherDispensations + (+formDrugUnits ?? 0)) > 5) {
                enqueueSnackbar(`There is an existing entry for this timepoint. The Fixed Dose Administration of 5 vials will be exceeded. Please check Timepoint and number of vials to be dispensed.`, { variant: 'error' });
                return;
            }

            if (otherDispensations + (+formDrugUnits ?? 0) < 5) {
                enqueueSnackbar(`Fixed Dose Administration. 5 vials to be used for this timepoint. Please ensure additional dispensation is added if a different batch is also used.`, { variant: 'warning' });
            }
        }

        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to save the drug dispensation: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave, drugDispensations, drugDispensation]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const [patientDrugs, , , ] = usePatientDrugs();

    const onNivoRelatlimabTreatment = patientDrugs?.find(x => x.drugId === Dtos.DrugTypes.NivolumabRelatlimab && x.patientTreatmentStatus === Dtos.PatientTreatmentStatus.OnTreatment);

    const onNivolumabTreatment = patientDrugs?.find(x => x.drugId === Dtos.DrugTypes.AdjuvantNivolumab && x.patientTreatmentStatus === Dtos.PatientTreatmentStatus.OnTreatment);

    const [selectedTimepoint, setSelectedTimepoint] = React.useState<Timepoint | undefined>(undefined);

    const timepointItems = React.useMemo(() => {
        if (!timepoints || timepoints.length === 0) {
            return [];
        }
        else {
            return timepoints?.map(timepoint => {
                return <MenuItem
                    key={timepoint.id}
                    value={timepoint.id}
                    disabled={(timepoint.value.includes("Adj") && !onNivolumabTreatment) || (!timepoint.value.includes("Adj") && !onNivoRelatlimabTreatment) }
                >
                    {timepoint.value}
                </MenuItem>;
            });
        }
    }, [timepoints, onNivolumabTreatment, onNivoRelatlimabTreatment]);

    const onTimepointChange = React.useCallback((timepointId: number) => {
        setSelectedTimepoint(timepoints?.find(x => x.id === timepointId));

        console.log(formRenderProps)
    }, [setSelectedTimepoint, timepoints, formRenderProps]);

    const [patient, , , ,] = React.useContext(PatientContext);

    const [timepointDrugs, setTimepointDrugs] = React.useState<Dtos.Drug[]>([]);

    React.useEffect(() => {
        const treatmentId = (patient as Dtos.Patient).treatmentId;

        if (treatmentId && (selectedTimepoint || drugDispensation?.timepointId)) {
            onlinePatientManagement.client
                .get(new Dtos.GetDrugsByTimepointAndTreatment({
                    timepointId: selectedTimepoint?.id ?? drugDispensation?.timepointId,
                    treatmentId: treatmentId
                }))
                .then(response => {
                    setTimepointDrugs(response.drugs);

                })
                .catch((e) => {
                    enqueueSnackbar('An error occurred while attempting to retrieve the drugs for the selected timepoint', { variant: 'error' });
                })
        }
    }, [selectedTimepoint, patient, setTimepointDrugs, enqueueSnackbar, drugDispensation]);

    const drugShipmentDepotBatchItems = React.useMemo(() => {
        if (!drugShipmentDepotBatches || drugShipmentDepotBatches.length === 0) {
            return [];
        }
        else {
            const availableBatches = drugShipmentDepotBatches.filter(dsb => dsb.drugShipment.shipmentStatus === Dtos.ShipmentStatus.Available);


            const validBatches = availableBatches.filter(dsb => dsb.drugShipment.shipmentStatus === Dtos.ShipmentStatus.Available)
                .filter(dsb => DateTime.fromISO(dsb.depotBatch.batch.allocationCutOffDate!) > DateTime.local().startOf('day'))
                .filter(dsb => (dsb.drugUnitsUsed! +
                    dsb.drugUnitsDamaged! +
                    dsb.drugUnitsDestroyed! +
                    dsb.drugUnitsLost! +
                    dsb.drugUnitsQuarantined!) < dsb.drugUnits!)
                .filter(dsb => patientDrugs?.find(pd => pd.patientTreatmentStatus === Dtos.PatientTreatmentStatus.OnTreatment && pd.drugId === dsb.depotBatch.batch.drugId));

            const distinctDepotBatches = selectDistinctObjects(availableBatches.map(dsdb => dsdb.depotBatch),
                (reduceObject, returnObject) => reduceObject.id === returnObject.id)
                .sort((a: Dtos.DepotBatch, b: Dtos.DepotBatch) => {
                    if (a.batch.drugId! > b.batch.drugId!) {
                        return 1;
                    }
                    else if (a.batch.drugId! < b.batch.drugId!) {
                        return -1;
                    }
                    else if (a.batch.dateExpiration! > b.batch.dateExpiration!) {
                        return 1;
                    }
                    else if (a.batch.dateExpiration! < b.batch.dateExpiration!) {
                        return -1;
                    }
                    else {
                        return a.id! - b.id!;
                    }
                });

            return distinctDepotBatches
                .filter(depotBatch => timepointDrugs.map(x => x.id).includes(depotBatch.batch.drugId) || selectedTimepoint?.value?.toLowerCase().includes('unscheduled')) 
                .map(depotBatch => {
                    return <MenuItem
                        key={depotBatch.id}
                        value={depotBatch.id}
                        disabled={!validBatches.find(x => x.depotBatch.id === depotBatch.id)}
                    >
                        {`${depotBatch.batch.drug.drugName} (${depotBatch.batch.batchNo} - ${depotBatch.depotBatchNo} Expires ${DateTime.fromISO(depotBatch.batch.dateExpiration!).toFormat('dd/MM/yyyy')})`}
                    </MenuItem>;
                });
        }
    }, [drugShipmentDepotBatches, drugDispensation, timepointDrugs]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    var formVoided = formRenderProps.form.getFieldState("voided")?.value;

    const { change } = useForm();

    React.useEffect(() => {
        if (!formRenderProps.form.getFieldState("voided")?.value) {
            change("notes", undefined);
        }
    }, [change, formRenderProps.form]);

    return (
        <>
            <DialogContent>
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12}>
                        {
                            timepointsLoadState && timepointsLoadState.state && timepointsLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="timepointId"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={formDisabled}
                                    label="Timepoint"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                    onChangeable={{
                                        onChange: (e: any) => { onTimepointChange(e.target.value) }
                                    }}
                                >
                                    {timepointItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="dateDispensation"
                            component={DatePickerWrapper}
                            type="text"
                            label="Date"
                            includeOffset={true}
                            removeTime={true}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {
                            drugShipmentDepotBatchesLoadState && drugShipmentDepotBatchesLoadState.state && drugShipmentDepotBatchesLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="depotBatchId"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={formDisabled}
                                    label="Batch"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                >
                                    {drugShipmentDepotBatchItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="drugUnits"
                            component={NumberTextField}
                            label={`${drugUnitName}`}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        
                        <FormControlLabel
                            control={
                                <Field
                                    fullWidth
                                    name="voided"
                                    component={CheckboxWrapper}
                                    label="Dispensation Voided"
                                    disabled={formDisabled}
                                />
                            }
                            label="Dispensation Voided"
                        />
                    </Grid>
                    {
                        !!formVoided && <Grid item xs={12}>
                            <Field
                                fullWidth
                                name="notes"
                                component={TextFieldWrapper}
                                label={`Notes`}
                                disabled={formDisabled}
                                multiline={true}
                                rows={5}
                                variant="outlined"
                                className={classes.textarea}
                            />
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const DrugDispensationModal: React.FunctionComponent<IDrugDispensationModalProps> = ({
    drugDispensation,
    timepoints,
    timepointsLoadState,
    drugShipmentDepotBatches,
    drugShipmentDepotBatchesLoadState,
    modalOpen,
    setModalOpen,
    onCloseClick,
    onFormSave
}) => {

    //const isEdit = !!drugDispensation && !!drugDispensation.id;

    const drugDispensationForm = React.useCallback((props: FormRenderProps<Dtos.DrugDispensation>) => {
        return (
            <DrugDispensationForm
                drugDispensation={drugDispensation ?? undefined}
                timepoints={timepoints}
                timepointsLoadState={timepointsLoadState}
                drugShipmentDepotBatches={drugShipmentDepotBatches}
                drugShipmentDepotBatchesLoadState={drugShipmentDepotBatchesLoadState}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [drugDispensation, timepoints, timepointsLoadState, drugShipmentDepotBatches, drugShipmentDepotBatchesLoadState, setModalOpen, onFormSave]);

    const asyncSave = useAsyncFunction(DrugDispensationActions.save, DrugDispensationActions.saveSuccess, DrugDispensationActions.saveFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Drug Dispensation
                </DialogTitle>
                <AsyncForm
                    initialValues={drugDispensation}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={drugDispensationForm}
                />
            </Dialog>
        </>
    );
}

export default DrugDispensationModal;